import React, { useMemo, useState } from 'react';
import { setFormValues } from 'hooks/useReduxForm';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';
import Icon from 'components/Utils/NewIcon';
import CPPUtils from 'services/CPPUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import CalendarUtils from 'services/CalendarUtils';
import DrugLabel from 'components/Utils/DrugLabel';
import './style.scss';
import DefaultModal from 'components/Utils/DefaultModal';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import { v4 as uuidV4 } from 'uuid';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import moment from 'moment-timezone';
import { MEDICATION_STATUS } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import Tooltip2 from 'components/Common/Tooltip2';
import CPPPatientMedicationInput from 'components/Patient/CPP/CPPInput/Inputs/CPPPatientMedicationInput';

const MEDICATION_STATUS_ARRAY = PerformanceUtils.objectToArray(MEDICATION_STATUS);

const CPPMedicationListElement = (props) => {
  const dispatch = useAppDispatch();
  const role = useAppSelector((state) => state.authentication.role);

  const formName = useMemo(
    () => CPPUtils.getFormName('prescription', props.patient.id),
    [props.patient.id],
  );
  const formData = useAppSelector((state) => state.rhfForms[formName]);

  const [printMedication, setPrintMedication] = useState();
  const [confirmationDialog, setConfirmationDialog] = useState();
  const allowEdit = role === 'physician' || role === 'admin';

  const items = useMemo(() => {
    let items = (props.items || []).filter((item) => CPPUtils.isHealthRecordItemEnabled(item));

    let activeMedications = CPPUtils.getActiveMedications(items);
    activeMedications = activeMedications.map((med) => {
      return {
        ...med,
        display_route: getRoute(med),
      };
    });
    activeMedications = PerformanceUtils.sortBy(activeMedications, [
      'display_route',
      'value.status',
    ]);
    let inactiveMedications = CPPUtils.getInactiveMedications(items);
    inactiveMedications = PerformanceUtils.sortBy(inactiveMedications, ['value.status']);

    return { active: activeMedications, inactive: inactiveMedications };
  }, [props.items]);

  function setActiveMedicationsTab() {
    if (!allowEdit) return;
    props.setActiveCPPTab('medications');
  }

  function refillMedication(medication) {
    let copy = PerformanceUtils.clone(medication);
    if (copy.value.prescription_medication !== undefined) {
      copy = CPPUtils.convertMedicationDosingInformation(copy.value.prescription_medication);
    } else {
      copy = { name: copy.value.name };
    }
    copy.id = uuidV4();
    editCPPItem('prescription', props.patient.id, copy);
  }

  function editCPPItem(activeTab, patientId, item) {
    props.setActiveCPPTab(activeTab);
    const medications = [...(formData?.medications || []), item];
    const dataToChange = { ...(formData || {}), medications };
    dispatch(setFormValues({ formName, values: dataToChange }));
  }

  function printLabel(medication) {
    let copy = PerformanceUtils.clone(medication);
    if (copy.value.prescription_medication !== undefined) copy = copy.value.prescription_medication;
    else copy = { name: copy.value.name };
    setPrintMedication(CPPUtils.convertMedicationDosingInformation(copy));
  }

  async function onConfirmChangeStatus(event, inputValue) {
    let currentDialog = { ...confirmationDialog };
    closeConfirmationDialog();
    return editMedication(currentDialog.medication, currentDialog.status, inputValue);
  }

  async function deleteMedication(medication) {
    await dispatch(
      api.HealthRecords.deleteHealthRecordMedication({
        medicationId: medication.id,
        patientId: props.patient.id,
      }),
      {
        errorTitle: 'Delete Medication',
      },
    );
  }

  async function editMedication(medication, status, notes) {
    if (status === MEDICATION_STATUS.print.name) {
      return printLabel(medication);
    } else if (status === MEDICATION_STATUS.renew.name) {
      return refillMedication(medication);
    } else if (status === MEDICATION_STATUS.delete.name) {
      return deleteMedication(medication);
    }

    let data = {
      id: medication.id,
      value: {
        ...medication.value,
        status,
        notes,
      },
    };

    await dispatch(
      api.HealthRecords.addOrEditHealthRecordPatientMedication({
        patientMedications: [data],
        patientId: props.patient.id,
      }),
      {
        errorTitle: 'Medication Update',
      },
    );
  }

  function closePrintMedication() {
    setPrintMedication();
  }

  function closeConfirmationDialog() {
    setConfirmationDialog();
  }

  function searchDocument(id) {
    if (props.onSearch) props.onSearch(id);
  }

  return (
    <>
      {printMedication !== undefined && (
        <DefaultModal
          wrap={true}
          className="fit-modal"
          title="Drug Label"
          padding={false}
          content={<DrugLabel patient={props.patient} medication={printMedication} />}
          isOpen={true}
          onRequestClose={closePrintMedication}
        />
      )}
      <div className="cpp-small-box medications-list">
        {!!confirmationDialog && (
          <ConfirmationDialog
            className="medication-dialog"
            key={!confirmationDialog}
            title="Change Medication Status"
            open={!!confirmationDialog}
            message={`Do you want to ${confirmationDialog?.status} ${confirmationDialog?.medication?.value?.name}?`}
            onConfirm={(ev, form) => onConfirmChangeStatus(ev, form?.['Reason for change'])}
            form={
              confirmationDialog?.reason
                ? [
                    {
                      type: 'autocomplete',
                      label: 'Reason for change',
                      inputs: confirmationDialog?.reason ? REASONS : undefined,
                    },
                  ]
                : undefined
            }
            onCancel={closeConfirmationDialog}
            loadingState={false}
          />
        )}
        <div className="top-info row">
          <Icon
            width="20px"
            height="20px"
            src="prescription-bottle"
            onClick={setActiveMedicationsTab}
          />
          <span className="small-title uppercase" onClick={setActiveMedicationsTab}>
            Medications
          </span>
        </div>
        <div className="list-content">
          <div className="list">
            <MedicationList
              items={items.active}
              setConfirmationDialog={setConfirmationDialog}
              editMedication={editMedication}
              allowEdit={allowEdit}
              searchDocument={searchDocument}
              extraKey={props.extraKey}
              patient={props.patient}
            />
            {items.inactive.length > 0 && !props.hideInactive && (
              <UncontrolledCollapse title={`Inactive (${items.inactive.length})`}>
                <MedicationList
                  items={items.inactive}
                  showRoute={false}
                  setConfirmationDialog={setConfirmationDialog}
                  editMedication={editMedication}
                  allowEdit={allowEdit}
                  searchDocument={searchDocument}
                  extraKey={props.extraKey}
                  patient={props.patient}
                />
              </UncontrolledCollapse>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const MedicationList = ({
  items = [],
  setConfirmationDialog,
  editMedication,
  allowEdit,
  searchDocument,
  showRoute = true,
  extraKey = '',
  patient,
}) => {
  const [editModal, setEditModal] = useState(null);

  let property = 'name';

  function getHistory(item) {
    let history = undefined;

    if (item.history && item.history.length > 0) {
      history = item.history.map((historyItem, index) => {
        let status = historyItem.value.status || MEDICATION_STATUS.continue.name;

        if (historyItem.value.notes) status += ` (${historyItem.value.notes})`;

        return (
          <span key={item.id + '-' + index} className="mt-1">
            {`${CalendarUtils.format(historyItem.date)} ${status}`}
            <br />
          </span>
        );
      });
    }
    return history;
  }

  let lastRoute = undefined;

  items = items.map((item, index) => {
    let text = item[property];
    let status = '';
    let lastsUntil = undefined;

    if (item.value !== undefined) {
      text = item.value[property];
      status = item.value.status || MEDICATION_STATUS.continue.name;

      let { prescription_medication, date } = item.value;

      date = date || item.history[0]?.date;

      if (prescription_medication !== undefined) {
        const dateUntil = CPPUtils.calculateDrugLastsUntil(prescription_medication, date);
        if (dateUntil) lastsUntil = dateUntil;
      }
    }

    let medicationColorClass = 'medication-active'; // Default

    if (lastsUntil) {
      const now = moment().unix();
      const twoWeeksFromNow = moment().add(2, 'weeks').unix();

      if (lastsUntil < now) {
        medicationColorClass = 'medication-ran-out';
      } else if (lastsUntil < twoWeeksFromNow) {
        medicationColorClass = 'medication-running-out';
      }
    }

    let tooltipText = (
      <div>
        <span>
          {text} ({status})
        </span>
        {lastsUntil && (
          <>
            <br />
            <span className={medicationColorClass}>
              Lasts until {CalendarUtils.format(lastsUntil)}
            </span>
          </>
        )}
      </div>
    );

    let history = getHistory(item);
    let route = item.display_route;

    let routeTitle =
      showRoute && route !== lastRoute ? (
        <span className="small-title list-element animated fadeIn">{route}</span>
      ) : null;
    lastRoute = route;

    let key = 'leftbar-list-' + (item.id || index) + extraKey;

    const medStatusArray = [...MEDICATION_STATUS_ARRAY];

    let documentId = item.value.prescription_medication?.document_id;
    if (documentId)
      medStatusArray.push({
        name: 'Show Prescription Document',
        searchDocument: true,
        value: documentId,
      });

    return (
      <React.Fragment key={key}>
        {routeTitle}
        <Tooltip2 tooltip={<Tooltip tooltipText={tooltipText} history={history} />}>
          {(register) => {
            return (
              <span
                className="tooltip-wrapper list-element list-element-medication truncate animated fadeIn"
                id={key}
                {...register}
              >
                {allowEdit && (
                  <ContextMenuTrigger id={key} holdToDisplay={0.00001}>
                    <Icon
                      src="gear"
                      width="15px"
                      alt="Change Status"
                      showTooltip={true}
                      className="status-icon"
                    />
                  </ContextMenuTrigger>
                )}
                <span className={`${status} ${medicationColorClass} row relative`}> {text} </span>
              </span>
            );
          }}
        </Tooltip2>
        <ContextMenu id={key}>
          <MenuItem disabled={true}>{text}</MenuItem>
          <MenuItem divider />
          {medStatusArray.map((currentStatus, index) => {
            function onClick() {
              let convertedItem = PerformanceUtils.clone(item);

              if (convertedItem.value.prescription_medication) {
                convertedItem.value.prescription_medication =
                  CPPUtils.convertMedicationDosingInformation(
                    convertedItem.value.prescription_medication,
                  );
              }

              if (currentStatus.reason) {
                return setConfirmationDialog({
                  medication: convertedItem,
                  status: currentStatus.name,
                  reason: true,
                });
              } else if (currentStatus.confirmDelete) {
                return setConfirmationDialog({
                  medication: convertedItem,
                  status: currentStatus.name,
                  reason: false,
                });
              } else if (currentStatus.searchDocument) {
                return searchDocument(currentStatus.value);
              } else if (currentStatus.edit) {
                setEditModal(convertedItem);
              } else {
                return editMedication(convertedItem, currentStatus.name);
              }
            }

            return (
              <MenuItem
                key={currentStatus.name + index}
                disabled={status === currentStatus.name}
                onClick={onClick}
              >
                {currentStatus.name}
              </MenuItem>
            );
          })}
        </ContextMenu>
      </React.Fragment>
    );
  });

  return (
    <>
      {editModal && (
        <DefaultModal
          className="add-medication-modal"
          title="Edit Medication"
          wrap={true}
          padding={true}
          draggable
          content={
            <CPPPatientMedicationInput
              key="medications"
              title="Medication"
              field="medications"
              onClose={() => setEditModal(false)}
              onSave={(medication) => {
                let notes = CPPUtils.getPrescriptionMedicationSummary(
                  medication.value.prescription_medication || medication.value,
                );
                editMedication(
                  { ...editModal, ...medication },
                  MEDICATION_STATUS.continue.name,
                  notes,
                );
                setEditModal(null);
              }}
              patient={patient}
              initialValue={editModal?.value?.prescription_medication || editModal?.value}
            />
          }
          isOpen={!!editModal}
          onRequestClose={() => setEditModal(null)}
        />
      )}
      {items}
    </>
  );
};

const Tooltip = ({ id, tooltipText, history }) => (
  <>
    {tooltipText}
    {history !== undefined && (
      <div>
        <br />
        <span>
          <b>History</b>
        </span>
        <br />
        {history}
      </div>
    )}
  </>
);

function getRoute(item) {
  let med = item?.value?.prescription_medication;
  let hasDrugbank = !!med?.drugbank_product;
  if (!hasDrugbank) return 'Unknown Interactions';
  let route = med?.route || med.drugbank_product?.route || 'Unknown Route';
  if (route.includes('nostril')) {
    route = 'Nasal';
  }
  if (route.includes('ear')) {
    route = 'Otic';
  }
  return route;
}

export default CPPMedicationListElement;

const REASONS = [
  'Treatment failure',
  'Patient request',
  'No longer required',
  'Treatment complete',
  'Alternate medication prescribed',
  'Drug interaction',
  'Risk of QT prolongation',
  'CP450 interaction',
  'Undesirable SE: hives',
  'Undesirable SE: rash',
  'Undesirable SE: SOB',
  'Undesirable SE: dizziness',
  'Undesirable SE: headache',
  'Undesirable SE: nausea',
  'Undesirable SE: vomitting',
  'Undesirable SE: GI upset',
  'Undesirable SE: GI cramping',
  'Undesirable SE: GI drowsiness',
];
